import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { registerRequest } from "../../store/features/user/userActions";
import { RootState } from "../../store/store";
import { clearRegisterState } from "../../store/features/user/userSlice";
import { FiArrowLeft, FiX } from "react-icons/fi";
import RegisterForm from "./RegisterForm";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface RegisterProps {
  isModal?: boolean;
  onSwitch?: () => void;
  onClose?: () => void;
}

export interface RegisterFormValues {
  [key: string]: any;
  email: string;
  mobile: string;
  // password: string;
  // confirmPassword: string;
  fullName: string;
  aadharNumber: string;
  panNumber: string;
  address: string;
  profileImage: string;
  imgExtenstion: string;
}

const Register: React.FC<RegisterProps> = ({ isModal, onSwitch, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [_emailState, setEmailState] = React.useState("");
  // const [passwordState, setPasswordState] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  // const location = useLocation();
  // const from = (location.state as any)?.from || "/";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    registerLoading,
    registerError,
    registerSuccessMessage,
    registerSuccess,
  } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (registerSuccess) {
      // Delay navigation to allow user to see success message
      const timer = setTimeout(() => {
        dispatch(clearRegisterState());
        // Prioritize onSwitch to toggle back to Login view within AuthPage
        if (onSwitch) {
            onSwitch();
        } else if (isModal && onClose) {
             // Fallback if onSwitch is missing but it's a modal (though AuthPage passes onSwitch)
             onClose();
        } else {
             // Fallback for standalone usage
             navigate("/auth", { replace: true });
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [
    registerSuccess,
    navigate,
    isModal,
    onClose,
    onSwitch,
    dispatch,
  ]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    // password: Yup.string()
    //   .min(6, "Password must be at least 6 characters")
    //   .required("Password is required"),
    // confirmPassword: Yup.string()
    //   .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    //   .required('Confirm Password is required'),
    fullName: Yup.string().required("Full name is required"),
    mobile: Yup.string()
      .matches(/^[0-9]+$/, "Must be only digits")
      .length(10, "Mobile number must be exactly 10 digits")
      .required("Mobile is required"),
    aadharNumber: Yup.string(),
    panNumber: Yup.string(),
    address: Yup.string(),
  });

  const formik = useFormik<RegisterFormValues>({
    initialValues: {
      email: "",
      mobile: "",
      // password: "",
      // confirmPassword: "",
      fullName: "",
      aadharNumber: "",
      panNumber: "",
      address: "",
      profileImage: "",
      imgExtenstion: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Exclude confirmPassword from payload
      // const { confirmPassword, ...rest } = values;
      const { ...rest } = values;
      const payload = {
        ...rest,
        profileImage: values.profileImage || "",
        imgExtenstion: values.imgExtenstion || "",
      };

      setEmailState(payload.email);
      // setPasswordState(payload.password);
      dispatch(registerRequest(payload));
    },
  });

  const handleRemoveImage = () => {
    formik.setFieldValue("profileImage", "");
    formik.setFieldValue("imgExtenstion", "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`relative h-screen w-full flex overflow-hidden bg-slate-50 font-sans`}>
        
       {/* LEFT SIDE - Hero/Art (Desktop Only) */}
       {!isModal && (
        <div className="hidden lg:flex lg:w-1/2 relative bg-indigo-600 overflow-hidden items-center justify-center">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
               <div className="absolute top-0 -left-1/4 w-full h-full bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob"></div>
               <div className="absolute -bottom-1/2 left-1/4 w-full h-full bg-gradient-to-tr from-indigo-400 to-emerald-400 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob animation-delay-2000"></div>
               <div className="absolute top-1/2 left-1/2 w-full h-full bg-gradient-to-bl from-pink-300 to-rose-300 rounded-full blur-3xl mix-blend-multiply filter opacity-70 animate-blob animation-delay-4000"></div>
               <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                   <filter id="noiseFilter">
                       <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch"/>
                   </filter>
                   <rect width="100%" height="100%" filter="url(#noiseFilter)" opacity="0.1"/>
               </svg>
            </div>
            
            {/* Content Overlay */}
            <div className="relative z-10 p-12 text-center text-white space-y-8 max-w-lg">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-lg rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-2xl border border-white/10">
                    <span className="text-3xl font-bold">A</span>
                </div>
                <h2 className="text-4xl font-extrabold tracking-tight leading-tight">
                    Join the  <br/> 
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-pink-200">Revolution</span>
                </h2>
                <p className="text-indigo-100 text-lg leading-relaxed font-light opacity-90">
                    Create your account today and unlock a world of seamless digital payments and financial freedom.
                </p>

                 {/* Decorative Graphic */}
                 <div className="relative mt-12 h-64 w-full flex justify-center">
                     <div className="w-64 h-64 bg-white/5 border border-white/10 rounded-full backdrop-blur-3xl absolute animate-pulse"></div>
                     <div className="w-48 h-48 bg-white/10 border border-white/20 rounded-full backdrop-blur-md absolute top-8 animate-bounce delay-100"></div>
                 </div>
            </div>
        </div>
      )}

      {/* RIGHT SIDE - Form */}
      <div className={`w-full ${!isModal ? 'lg:w-1/2' : ''} h-full overflow-y-auto custom-scrollbar flex flex-col p-4 sm:p-6 pb-[5px] relative`}>
         {/* Soft Background for Mobile/Right Side */}
        <div className="absolute inset-0 -z-10 bg-white">
             <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-indigo-50/50 blur-3xl"></div>
             <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-purple-50/50 blur-3xl"></div>
        </div>

        {!isModal && (
            <RouterLink
              to="/"
              className="absolute top-6 left-6 lg:top-12 lg:left-12 flex items-center gap-2 text-gray-400 hover:text-violet-600 transition-colors font-medium text-sm group"
            >
              <span className="p-2 rounded-full bg-gray-50 group-hover:bg-violet-50 transition-colors">
                  <FiArrowLeft size={16} />
              </span>
              <span className="hidden sm:inline">Back to Home</span>
            </RouterLink>
          )}

        {isModal && onClose && (
            <button
                onClick={onClose}
                className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-full transition-all z-50"
            >
                <FiX size={24} />
            </button>
        )}

        {/* Content Container */}
        <div className="w-full max-w-md mx-auto space-y-4 pt-10 pb-10 lg:pt-0 my-auto">
             <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                    Create Account
                </h1>
                <p className="text-gray-400 text-sm">
                    Enter your details to get started.
                </p>
            </div>

            <RegisterForm 
                formik={formik}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
                handleRemoveImage={handleRemoveImage}
                fileToBase64={fileToBase64}
                registerLoading={registerLoading}
                registerError={registerError}
                registerSuccessMessage={registerSuccessMessage}
            />

            <div className="pt-6 text-center">
                <p className="text-sm text-gray-500">
                    Already have an account?
                    {isModal ? (
                    <button
                        onClick={onSwitch}
                        className="ml-1 font-semibold text-violet-600 hover:text-violet-500 transition-colors hover:underline"
                    >
                        Sign in
                    </button>
                    ) : (
                    <RouterLink
                        to="/auth"
                        className="ml-1 font-semibold text-violet-600 hover:text-violet-500 transition-colors hover:underline"
                    >
                        Sign in
                    </RouterLink>
                    )}
                </p>
            </div>
        </div>

      </div>
    </div>
  );
};

export default Register;
